import Box from "@mui/joy/Box"
import CircularProgress from '@mui/material/CircularProgress';
import Button from "@mui/joy/Button"
import Input from "@mui/joy/Input"
import { Octokit } from "octokit"
import { Endpoints } from "@octokit/types"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { stealHashQuery } from "./app"
import { TTMetadata } from "./tinytapeout_metadata"
import { Table, TableCell, TableRow } from "@mui/material";

export type GithubState = { kind: "initial" } | { kind: "awaitAuth" } | { kind: "checkInstalled" } | { kind: "notInstalled" } | { kind: "active", app: Octokit } | { kind: "authFailed", error: string }

const CLIENT_ID = "Iv23liiPqG3dzL32Dt9h"
const AUTH_URL = `https://github.com/login/oauth/authorize?client_id=${CLIENT_ID}`
const GATEKEEPER_URL = "https://packages.spade-lang.org"

type Artefacts = {
  verilogProduct: string | null,
  spadeCode: string,
}

class TypedOctokit {
  constructor(inner: Octokit) {
    this.inner = inner
  }
  inner: Octokit

  async request<const Endpoint extends keyof Endpoints>(endpoint: Endpoint, params: Endpoints[Endpoint]["parameters"]): Promise<Endpoints[Endpoint]["response"]> {
    return this.inner.request(endpoint, params)
  }
}

export const initialGithubState = () => {
  return { kind: "initial" }
}

function octokitFromAuth(auth: string): TypedOctokit {
  return new TypedOctokit(new Octokit({ auth: auth }))
}

// If we have a code from the URL, we'll need to ask our server to conver that into an authentication token which we can
// use later.
export const githubStateSetupEffect = (setState: Dispatch<SetStateAction<GithubState>>) => {
  return () => {
    async function run() {
      const url = new URL(window.location.href)
      const code = url.searchParams.get("code")
      const setup_action = url.searchParams.get("setup_action")
      const existingAuth = localStorage.getItem("github_auth_token");
      var octokit: TypedOctokit | null = null;
      if (existingAuth) {
        octokit = octokitFromAuth(existingAuth);
        setState({ kind: "checkInstalled" })
      } else if (code && setup_action == null) {
        console.log("Requesting code resolution");
        await fetch(`${GATEKEEPER_URL}/github_authenticate/${code}`)
          .then((response) => response.body)
          .then((body) => {
            return body.getReader().read().then((content) => {
              const token = new TextDecoder().decode(content.value)

              if (token) {
                localStorage.setItem("github_auth_token", token)
                window.location.search = ""
                octokit = octokitFromAuth(existingAuth);
                setState({ kind: "checkInstalled" })
              }
            })
          })
      }

      if (!octokit) {
        return
      }

      // After code check, check if the user has our app installed
      const { data: { installations } } = await octokit.request("GET /user/installations", {})

      const myAppSlug = "spade-playground"; // or check app_id
      const isInstalled = installations.some(inst => inst.app_slug === myAppSlug);

      console.log("Is installed: ", isInstalled)

      if (!isInstalled) {
        setState({ kind: "notInstalled" })
      } else {
        setState({ kind: "active", app: octokit })
      }

    }
    run()
  }
}

const DUMMY_TEST_MAKEFILE = `test:
	echo "No tests run since this is a Spade playground project"
	echo '<testsuites name="results"><testsuite name="all" package="all"><property name="random_seed" value="1732265775" /></testsuite></testsuites>' > results.xml

clean:
	echo "No-op"`;

async function createTTRepo(username: string, repoName: string, octokit: TypedOctokit, statusUpdate: (arg0: RepoStatus) => void) {
  await octokit.request('POST /repos/{template_owner}/{template_repo}/generate', {
    template_owner: 'TinyTapeout',
    template_repo: 'ttsky-verilog-template',
    owner: username,
    name: repoName,
    description: 'TinyTapeout submission from https://play.spade-lang.org/',
    include_all_branches: false,
    'private': false,
  })
  statusUpdate({ kind: "WaitContent" })

  // It seems to take a while for the clone to take place, so let's give it some time
  const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
  await delay(2000)

  statusUpdate({ kind: "Exists" })
}

async function pushTTRepo(user: string, repoName: string, octokit: TypedOctokit, artefacts: Artefacts, yaml: string, onDone: (arg0: PushState) => void) {
  onDone({ kind: "Pushing" })
  const file_map = {
    "src/spade.sv": artefacts.verilogProduct,
    "src/playground.spade": artefacts.spadeCode,
    "info.yaml": yaml,
    "test/Makefile": DUMMY_TEST_MAKEFILE,
  }

  for (const file in file_map) {
    var sha = null;
    var content = null;
    try {
      const { data: response } = await octokit.request("GET /repos/{owner}/{repo}/contents/{path}", {
        owner: user,
        repo: repoName,
        path: file
      })

      if ('type' in response && response.type == "file") {
        sha = response.sha
        content = response.content
      } else {
        console.log(file + " was not a file")
      }
      // Assume 404 if not ok
    } catch {
    }

    // We want to avoid pushing files that we're not going to change since any push
    // creates a commit, regardless of changes
    const oldContent = content ? content.replaceAll(" ", "").replaceAll("\n", "").replaceAll("\r", "") : null
    const newContent = btoa(file_map[file]).replaceAll(" ", "").replaceAll("\n", "").replaceAll("\r", "")
    if (oldContent != newContent) {
      await octokit.request("PUT /repos/{owner}/{repo}/contents/{path}", {
        owner: user,
        repo: repoName,
        path: file,
        message: "Push from play.spade-lang.org",
        sha: sha,
        commiter: {
          name: user,
          email: user + "@play.spade-lang.org"
        },
        content: newContent
      })
    }
  }

  onDone({ kind: "Pushed" })
}

function ActionStatus({ user, repo, octokit }: { user: string, repo: string, octokit: TypedOctokit }) {
  const [workflowRuns, setWorkflowRuns] = useState<{
    id: number,
    name: string,
    url: string,
    status: string,
    conclusion: string,
    head_commit: string,
    started_at: string
  }[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      const inner = async () => {
        const { data: { workflow_runs: result } } = await octokit.request("GET /repos/{owner}/{repo}/actions/runs", { repo: repo, owner: user })

        setWorkflowRuns(result.map((run) => {
          return {
            id: run.id,
            name: run.name,
            url: run.html_url,
            status: run.status,
            conclusion: run.conclusion,
            head_commit: run.head_commit.id,
            started_at: run.run_started_at
          }
        }))
      }
      inner()
    }, 1_000)

    return () => clearInterval(interval)
  })

  return <Box>
    <p>Workflow runs:</p>
    <p>If you haven't done so, enable github pages from the github actions source <a href={`https://github.com/${user}/${repo}/settings/pages`}>in your repo settings</a></p>
    <table>
      <tbody>
        {
          workflowRuns.map((run) => {
            const status = () => {
              switch (run.status) {
                case "completed":
                  switch (run.conclusion) {
                    case "completed":
                      return <code style={{ color: "green" }}>Completed</code>
                    case "failure":
                      return <code style={{ color: "red" }}>Failed</code>
                    default:
                      return <code>{run.conclusion}</code>
                  }
                default:
                  return <code>{run.status}</code>
              }
            }
            return <tr key={run.id}>
              <td>{run.name}</td>
              <td>{status()}</td>
              <td><a href={run.url}>View on GitHub</a></td>
              <td><code>{run.started_at}</code></td>
            </tr>
          })
        }
      </tbody>
    </table>
  </Box>
}

type RepoStatus = { kind: "Unknown" } | { kind: "DoesNotExist" } | { kind: "WaitContent" } | { kind: "WrongContent" } | { kind: "Exists" }
type PushState = { kind: "Unpushed" } | { kind: "Pushing" } | { kind: "Pushed" }

export function ActiveGithubPushComponent({ octokit, artefacts }: { octokit: TypedOctokit, artefacts: Artefacts }) {
  const [user, setUser] = useState(null)
  const [repoName, setRepoName] = useState(localStorage.getItem("tt-repo-name") ? localStorage.getItem("tt-repo-name") : "")
  const [repoStatus, setRepoStatus] = useState<RepoStatus>(null)
  const [yaml, setYaml] = useState<string>()
  const [pushState, setPushState] = useState<PushState>({ kind: "Unpushed" })

  useEffect(() => {
    localStorage.setItem("tt-repo-name", repoName)
  }, [repoName])

  useEffect(() => {
    setPushState({ kind: "Unpushed" })
  }, [artefacts.verilogProduct])

  useEffect(() => {
    async function inner() {
      const { data: { login } } = await octokit.inner.rest.users.getAuthenticated();

      setUser(login)
    }
    inner()
  });

  useEffect(() => {
    const thisName = repoName;
    setRepoStatus({ kind: "Unknown" })
    async function inner() {
      if (user && repoName) {
        try {
          const { data: { } } = await octokit.request("GET /repos/{owner}/{repo}", {
            owner: user,
            repo: repoName,
          })
        } catch (e) {
          if (thisName == repoName) {
            setRepoStatus({ kind: "DoesNotExist" })
          }
          return
        }

        // We know the repo exists, let's get its content to do a sniff test if this is a TT repo
        const { data: contentResponse } = await octokit.request("GET /repos/{owner}/{repo}/contents/{path}", {
          owner: user,
          repo: repoName,
          path: "",
        })

        var hasSpadeFile = false
        if (contentResponse instanceof Array) {
          const isTt = contentResponse.some((elem) => {
            console.log("Found " + elem.name)
            return elem.name == "info.yaml"
          })

          if (thisName == repoName) {
            if (isTt) {
              setRepoStatus({ kind: "Exists" })
            } else {
              setRepoStatus({ kind: "WrongContent" })
              return
            }
          }

        }
      }
    }
    inner()
  }, [repoName, user])

  const CreateButton = () => {
    if (user) {
      switch (repoStatus.kind) {
        case null:
          if (repoName) {
            return <p>Looking up repo</p>
          } else {
            return
          }
        case "DoesNotExist":
          return <Box display="flex" flexDirection={"row"}>
            <Button onClick={() => {
              createTTRepo(user, repoName, octokit, setRepoStatus)
              setRepoStatus({ kind: "Unknown" })
            }}>
              Create new repo
            </Button>
          </Box>
        case "WaitContent":
          return <p>Looking up repo content</p>
        case "WrongContent":
          return <p>This repo exists, but it does not have an info.yaml file, try another name</p>
        case "Exists":
          const pushLogic = () => {
            if (!artefacts.verilogProduct) {
              return <p style={{ color: "red" }}>Before pushing to tinytapeout, you have to <b>build</b> your Spade code</p>
            } else {
              const pushButton = yaml
                ? <Button onClick={() => {
                  pushTTRepo(user, repoName, octokit, artefacts, yaml, (p) => { setPushState(p) })
                }}>Push</Button>
                : null

              const pushStatus = () => {
                switch (pushState.kind) {
                  case "Unpushed": return <Box></Box>;
                  case "Pushing": return <Box><CircularProgress /></Box>
                  case "Pushed": return <Box><p>✅</p></Box>
                }
              }

              return <Box>
                {pushButton}
                {pushStatus()}
              </Box>
            }
          }


          return <Box>
            {pushLogic()}
            <ActionStatus user={user} repo={repoName} octokit={octokit} />
          </Box>
      }
    }
  }

  return <Box>
    <TTMetadata user={user} onYamlChange={setYaml} spadeCode={artefacts.spadeCode} />
    <p>
      Current user: {user}
    </p>
    {
      user ?
        <Box>
          <label>Repo name:</label>
          <Input value={repoName} onChange={(event) => setRepoName(event.target.value)} placeholder="Repo name" />
          <CreateButton />
        </Box>
        : null
    }
  </Box>
}


export function GithubPushComponent({ githubState, setGithubState, artefacts }: { githubState: GithubState, setGithubState: Dispatch<SetStateAction<GithubState>>, artefacts: Artefacts }) {
  switch (githubState.kind) {
    case "initial":
      return <Box>
        <p>To push to TinyTapeout, you need to <a href={AUTH_URL}>authenticate with github</a></p>
      </Box>
    case "awaitAuth":
      return <p>Finalizing authentication...</p>
    case "checkInstalled":
      return <p>Checking if the app is installed on your github account...</p>
    case "notInstalled":
      return <p>Next, <a href="https://github.com/apps/spade-playground">install the spade-playground app on your github account</a></p>
    case "active":
      return <ActiveGithubPushComponent octokit={githubState.app} artefacts={artefacts} />
  }
}

